import { decodeFA } from "@swivu/shared-ui";
import ThemeMenu from "./Theme.js";
import { motion } from "motion/react";
import { Link, useParams } from "react-router-dom";
import { usePublicViewsWithSuspense } from "@swivu/client-api";
import { generateViewLink } from "../lib/generateViewLink.js";

const SidebarSelected = ({ matchId }: { matchId: string | undefined }) => {
  const { id: paramId } = useParams<{
    entity: string | undefined;
    id: string | undefined;
  }>();

  let isActuallySelected: boolean;
  if (matchId === undefined) {
    // This instance is for the "Team scheduler" (or a "home" link)
    isActuallySelected = paramId === undefined;
  } else {
    // This instance is for a specific view link
    isActuallySelected = paramId === matchId;
  }

  if (!isActuallySelected) {
    return null;
  }
  return (
    <motion.div
      className="dark:bg-mono-900  shadow-lg/5 dark:border-mono-700/50 absolute left-[-10px] top-[-6px] z-10 w-full rounded-md border bg-white p-4 md:left-[0px]"
      layoutId={`sidebar-view`}
      transition={{
        type: "spring",
        bounce: 0,
        duration: 0.3
      }}
    />
  );
};

const SidebarContent = () => {
  const { slug, id: _id } = useParams<{ slug: string; id: string }>();
  const { data } = usePublicViewsWithSuspense({
    variables: {
      slug: slug as string
    }
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col justify-between">
      <div className="flex min-h-0 flex-1 flex-col ">
        <Link
          to={`/p/${slug}`}
          className="relative flex items-center justify-between hover:underline md:pl-4 md:pt-0.5"
        >
          <SidebarSelected matchId={undefined} />
          <div className="relative z-10 flex items-center gap-3 pl-3 text-sm font-bold md:pl-0">
            <i
              className={`size-4 text-mono-700 dark:text-mono-400 fa-regular ${decodeFA(
                `calendar`
              )}`}
            />
            <span>Team scheduler</span>
          </div>
        </Link>
        {data.length > 0 && (
          <div className="relative mt-3 flex min-h-0 flex-1  flex-col overflow-y-auto pl-3 md:pl-0">
            <h4 className="dark:bg-mono-950 sticky top-0 z-[5] -ml-4 flex-none  pb-2 pl-4  pt-6 text-sm font-bold  md:ml-0">
              <span className="opacity-50">Shared views</span>
            </h4>
            <div className="h-2 flex-none" />
            {/* Wrap with AnimateSharedLayout for shared layoutId animation */}
            <ul className="z-1 relative mt-3 flex flex-1 flex-col gap-4 md:mt-0">
              {data.map((view) => (
                <li key={view.id} className="">
                  <Link
                    to={generateViewLink({
                      slug: slug as string,
                      id: view.id.toString(),
                      available_stores: view.available_stores
                    })}
                    className="relative flex items-center justify-between hover:underline md:pl-4"
                  >
                    <SidebarSelected matchId={view.id.toString()} />
                    <div className="relative z-10 flex  items-center gap-3 text-sm font-bold">
                      {view.icon && (
                        <i
                          className={`size-4 text-mono-700 dark:text-mono-400 fa-regular ${decodeFA(
                            view.icon
                          )}`}
                        />
                      )}
                      <span>{view.name}</span>
                    </div>
                  </Link>
                </li>
              ))}
              <li className="h-6 list-none" />
            </ul>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4 pb-4 pl-3 md:pl-4">
        <div className="border-mono-200 dark:border-mono-800 border-t" />
        <ThemeMenu />
      </div>
    </div>
  );
};

SidebarContent.Loading = () => {
  const data = Array.from({ length: 3 }, (_, i) => ({
    id: i,
    name: `View ${i}`,
    icon: "fa-solid fa-circle-dot"
  }));
  return (
    <div className="flex flex-1 animate-pulse flex-col justify-between">
      <ul className="flex flex-1 flex-col gap-2">
        {data.map((view) => (
          <li key={view.id} className="flex w-full">
            <div className="flex  flex-1 items-center gap-4 hover:underline">
              <div className="bg-mono-200 size-4 dark:bg-mono-900">
                <i className={`fa-regular  opacity-0 ${decodeFA(view.icon)}`} />
              </div>
              <div className="bg-mono-200 dark:bg-mono-900 h-6 w-24 flex-1 rounded-md" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SidebarContent;
